import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useOrg } from "@/context/OrgContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { CheckCircle, Circle, UsersFour, Buildings, TrendUp, Plus, Rocket, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Clients() {
  const { t } = useLang();
  const { switchOrg, refresh: refreshOrgs } = useOrg();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({
    org_name: "",
    country: "",
    industry: "",
    reporting_year: new Date().getFullYear(),
    invite_email: "",
    invite_name: "",
    invite_role: "editor",
  });
  const [creating, setCreating] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const load = async () => {
    const { data } = await api.get("/consulting/overview");
    setData(data);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.org_name.trim()) return;
    setCreating(true);
    try {
      const payload = { ...form };
      if (!payload.invite_email) delete payload.invite_email;
      const { data: r } = await api.post("/consulting/clients", payload);
      toast.success(t("clientCreated"));
      if (r.invite && r.invite.temporary_password) {
        setInviteResult(r.invite);
      } else {
        setShowDialog(false);
      }
      setForm({ org_name: "", country: "", industry: "", reporting_year: new Date().getFullYear(), invite_email: "", invite_name: "", invite_role: "editor" });
      await refreshOrgs?.();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setCreating(false);
    }
  };

  const openClient = async (orgId) => {
    if (switchOrg) switchOrg(orgId);
    else localStorage.setItem("cl_active_org", orgId);
    nav("/app");
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const p = data.portfolio;

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Enasuspro® Sustainability Coaching</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("clientsTitle")}</h1>
          <p className="text-muted-foreground text-sm mt-2">{t("clientsDesc")}</p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="clients-add-btn">
          <Plus size={16} className="mr-2" /> {t("clientsAddNew")}
        </Button>
      </div>

      {/* Portfolio KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi icon={UsersFour} label={t("clientsCount")} value={p.client_count} testid="kpi-clients" />
        <Kpi icon={CheckCircle} label={t("clientsFullyOnboarded")} value={p.fully_onboarded} suffix={`/ ${p.client_count}`} testid="kpi-onboarded" />
        <Kpi icon={TrendUp} label={t("clientsTotalEmissions")} value={p.total_tco2e_current_year.toFixed(1)} suffix="tCO₂e" testid="kpi-total-emissions" />
        <Kpi icon={Warning} label={t("clientsPending")} value={p.total_pending_approvals} suffix={t("approvals")} testid="kpi-pending" accent={p.total_pending_approvals > 0} />
      </div>

      {/* Clients grid */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <Buildings size={20} weight="duotone" className="text-primary" />
            {t("clientsList")} — {data.clients.length}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.clients.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">{t("clientsEmpty")}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.clients.map((c) => (
                <ClientCard key={c.org_id} client={c} steps={data.onboarding_steps} onOpen={() => openClient(c.org_id)} t={t} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create client dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="clients-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Rocket size={20} weight="duotone" className="text-primary" />
              {t("clientsNewTitle")}
            </DialogTitle>
          </DialogHeader>
          {inviteResult ? (
            <div className="space-y-3" data-testid="invite-result">
              <div className="rounded-md border border-primary/40 bg-primary/5 p-4 space-y-2">
                <div className="text-sm font-medium">{t("clientsInviteReady")}</div>
                <div className="text-xs font-mono-data space-y-1">
                  <div>Email: <span className="text-foreground">{inviteResult.email}</span></div>
                  {inviteResult.temporary_password && (
                    <div>Password: <span className="text-primary font-medium">{inviteResult.temporary_password}</span></div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">{t("clientsInviteHint")}</div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setInviteResult(null); setShowDialog(false); }} data-testid="invite-done">{t("close")}</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5 md:col-span-2">
                  <Label>{t("clientsOrgName")}</Label>
                  <Input value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} data-testid="client-org-name" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("country")}</Label>
                  <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g. Türkiye" data-testid="client-country" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("industry")}</Label>
                  <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="e.g. Manufacturing" data-testid="client-industry" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("reportingYear")}</Label>
                  <Input type="number" value={form.reporting_year} onChange={(e) => setForm({ ...form, reporting_year: parseInt(e.target.value) })} data-testid="client-year" />
                </div>
              </div>

              <div className="border-t border-border pt-3">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mb-2">{t("clientsInviteOptional")}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t("email")}</Label>
                    <Input type="email" value={form.invite_email} onChange={(e) => setForm({ ...form, invite_email: e.target.value })} placeholder="client@example.com" data-testid="client-invite-email" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("name")}</Label>
                    <Input value={form.invite_name} onChange={(e) => setForm({ ...form, invite_name: e.target.value })} data-testid="client-invite-name" />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="client-cancel">{t("cancel")}</Button>
                <Button onClick={create} disabled={creating || !form.org_name.trim()} data-testid="client-submit">
                  {creating ? "…" : t("clientsCreate")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClientCard({ client, steps, onOpen, t }) {
  const pct = client.onboarding_pct || 0;
  const barColor = pct === 100 ? "bg-primary" : pct >= 60 ? "bg-primary/70" : pct >= 40 ? "bg-accent" : "bg-destructive/60";
  return (
    <div className="border border-border rounded-md p-4 space-y-3 hover:border-primary/40 transition-colors" data-testid={`client-card-${client.org_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <button onClick={onOpen} className="text-left w-full" data-testid={`client-open-${client.org_id}`}>
            <div className="font-medium truncate hover:text-primary transition-colors">{client.name}</div>
            <div className="text-xs text-muted-foreground font-mono-data mt-0.5">
              {[client.country, client.industry].filter(Boolean).join(" · ") || "—"}
            </div>
          </button>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("clientsOnboarding")}</div>
          <div className={`font-display text-xl font-semibold ${pct === 100 ? "text-primary" : ""}`} data-testid={`client-pct-${client.org_id}`}>
            {pct}%
          </div>
        </div>
      </div>

      <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>

      <div className="grid grid-cols-5 gap-1">
        {steps.map((step) => {
          const done = client.checklist?.[step.key];
          const Icon = done ? CheckCircle : Circle;
          return (
            <div key={step.key} className="flex flex-col items-center gap-0.5 text-center" title={step.label} data-testid={`client-step-${client.org_id}-${step.key}`}>
              <Icon size={16} weight={done ? "fill" : "regular"} className={done ? "text-primary" : "text-muted-foreground/50"} />
              <div className="text-[9px] uppercase font-mono-data text-muted-foreground truncate w-full">{step.label.split(" ")[0]}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs font-mono-data pt-2 border-t border-border/50">
        <div>
          <div className="text-muted-foreground text-[10px] uppercase">{t("scope123")}</div>
          <div className="text-sm font-medium">{client.total_tco2e_current_year.toFixed(2)} tCO₂e</div>
        </div>
        <div>
          <div className="text-muted-foreground text-[10px] uppercase">{t("records")}</div>
          <div className="text-sm font-medium">{client.approved_entries}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-[10px] uppercase">{t("clientsPending")}</div>
          <div className={`text-sm font-medium ${client.pending_approvals > 0 ? "text-destructive" : ""}`}>{client.pending_approvals || 0}</div>
        </div>
      </div>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, suffix, testid, accent }) => (
  <Card className={accent ? "border-destructive/40" : undefined} data-testid={testid}>
    <CardContent className="p-5">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-2">
        <Icon size={14} weight="duotone" className={accent ? "text-destructive" : "text-primary"} />
        {label}
      </div>
      <div className="font-display text-3xl font-semibold mt-2 font-mono-data leading-none">
        {value}
        {suffix && <span className="text-sm text-muted-foreground ml-1 font-normal">{suffix}</span>}
      </div>
    </CardContent>
  </Card>
);
